import { Alert, Box, Chip, Link, MenuItem, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { ACCOUNT_TYPE_FILTERS, CUSTOMER_SEARCH_FIELDS } from '@app/shared';
import type { CustomerListItemDto, CustomerSearchField } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { PageHeader } from '../../components/PageHeader';
import { StatusChip } from '../../components/StatusChip';
import { useFeatureReleased } from '../../release';
import { useCustomerSearch } from './api';
import { CUSTOMER_SECTIONS, CustomerRowLinks, customerPath } from './parts';

/** 10 rows per page, as in the reference. */
const PAGE_SIZE = 10;

/**
 * Longest value each field takes. Phone, account, enroll ID, names and alternate contact are
 * the reference's own limits; the reference sets none for the other four, so those are the
 * limits of the API.
 */
const MAX_LENGTH: Record<CustomerSearchField, number> = {
  phone: 10,
  accountNo: 12,
  enrollmentId: 12,
  firstName: 20,
  lastName: 20,
  email: 120,
  sim: 25,
  imei: 25,
  tag: 40,
  alternateContact: 10,
};
const NUMERIC: CustomerSearchField[] = ['phone', 'sim', 'imei', 'alternateContact'];

type Criteria = Record<CustomerSearchField | 'accountType', string>;
const EMPTY = {
  ...Object.fromEntries(CUSTOMER_SEARCH_FIELDS.map(([key]) => [key, ''])),
  accountType: '',
} as Criteria;

/** `open` is where a result leads: the profile, or the customer page of a menu entry. */
const columnsFor = (open: (id: string) => string): Column<CustomerListItemDto>[] => [
  {
    key: 'customerId',
    header: 'Cust ID',
    sortable: true,
    render: (c) => (
      <Link component={RouterLink} to={open(c.id)} data-customer-id={c.id}>
        {c.customerId}
      </Link>
    ),
  },
  { key: 'enrollmentId', header: 'Enrollment ID', render: (c) => c.enrollmentId },
  { key: 'firstName', header: 'First Name', render: (c) => c.firstName },
  { key: 'lastName', header: 'Last Name', sortable: true, render: (c) => c.lastName },
  { key: 'serviceAddress', header: 'Service Address', render: (c) => c.serviceAddress },
  { key: 'city', header: 'City', render: (c) => c.city },
  { key: 'state', header: 'State', render: (c) => c.state },
  { key: 'zip', header: 'Zip', render: (c) => c.zip },
  { key: 'sim', header: 'ESN Number', nowrap: true, render: (c) => c.sim },
  { key: 'mdn', header: 'Telephone', nowrap: true, render: (c) => c.mdn },
  { key: 'enrollmentType', header: 'Enrollment Type', render: (c) => c.enrollmentType },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (c) => <StatusChip status={c.status} />,
  },
];

/**
 * Customer search (PC56): the ten Advanced Search fields and the result list.
 *
 * The criteria live in the URL (`/customers?firstName=…`), so the header's "Search Customer"
 * box (`?q=…`) and its Advanced Search dialog only have to navigate here, and a search can be
 * reloaded or returned to with the browser's Back button.
 *
 * A search with a single result still shows the list, as the reference does; it is not
 * opened automatically.
 *
 * The Customer Service entries of the sidebar (Billing and Invoice, Order History, ...) open
 * this same search, as in the reference; a result then opens that page of the customer
 * (`/customers/find/:section`).
 */
export function CustomerSearchPage() {
  const [params, setParams] = useSearchParams();
  const featureOn = useFeatureReleased();
  const { section } = useParams();
  const target = CUSTOMER_SECTIONS.find((s) => s.key === section);
  const open = useMemo(
    () => (id: string) => (target ? `${customerPath(id)}/${target.path}` : customerPath(id)),
    [target],
  );
  const columns = useMemo(() => columnsFor(open), [open]);

  // What the URL asks for.
  const applied = useMemo(() => {
    const criteria = { ...EMPTY };
    for (const [key] of CUSTOMER_SEARCH_FIELDS) criteria[key] = params.get(key)?.trim() ?? '';
    criteria.accountType = params.get('accountType') ?? '';
    return criteria;
  }, [params]);
  const q = params.get('q')?.trim() ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const sort = params.get('sort') ?? undefined;
  const hasCriteria = Boolean(q) || Object.values(applied).some(Boolean);

  // What the form shows; it follows the URL when a search arrives from the header.
  const [draft, setDraft] = useState<Criteria>(applied);
  const [empty, setEmpty] = useState(false);
  useEffect(() => {
    setDraft(applied);
    setEmpty(false);
  }, [applied]);

  const results = useCustomerSearch(
    { ...applied, q, page, pageSize: PAGE_SIZE, sort },
    hasCriteria,
  );

  const search = () => {
    const filled = [
      ...CUSTOMER_SEARCH_FIELDS.map(([key]) => [key, draft[key].trim()] as const),
      ['accountType', draft.accountType] as const,
    ].filter(([, value]) => value);
    // The API refuses a search without a value; say so without asking it.
    setEmpty(filled.length === 0);
    // A new search starts on page 1 and replaces the header's search text.
    if (filled.length > 0) setParams(new URLSearchParams(filled.map(([k, v]) => [k, v])));
  };
  const reset = () => {
    setDraft(EMPTY);
    setEmpty(false);
    setParams(new URLSearchParams());
  };
  /** Changes paging or sorting and keeps the criteria. */
  const update = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next);
  };

  return (
    <Box>
      <PageHeader title={target ? `Search - ${target.label}` : 'Search'} code={target?.code} />
      {target ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Find the customer; the result opens {target.label}.
        </Alert>
      ) : null}
      <FilterPanel onSearch={search} onReset={reset}>
        {CUSTOMER_SEARCH_FIELDS.map(([key, label]) => (
          <TextField
            key={key}
            label={label}
            value={draft[key]}
            onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
            autoComplete="off"
            slotProps={{
              htmlInput: {
                maxLength: MAX_LENGTH[key],
                inputMode: NUMERIC.includes(key) ? 'numeric' : undefined,
              },
            }}
          />
        ))}
        {featureOn('account-type-filters') ? (
          <TextField
            select
            label="Account Type"
            value={draft.accountType}
            onChange={(e) => setDraft((d) => ({ ...d, accountType: e.target.value }))}
          >
            <MenuItem value="">All</MenuItem>
            {ACCOUNT_TYPE_FILTERS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </FilterPanel>

      {empty ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Enter at least one search value
        </Alert>
      ) : null}

      {q ? (
        <Box sx={{ mb: 1 }}>
          <Chip
            label={`Search Customer: ${q}`}
            onDelete={() => update({ q: undefined, page: undefined })}
          />
        </Box>
      ) : null}

      {/* Before a search there is nothing to list. */}
      {hasCriteria ? (
        <CustomerRowLinks open={open}>
          <DataTable
            label="Customer search results"
            columns={columns}
            rows={results.data?.data}
            rowKey={(c) => c.id}
            loading={results.isFetching}
            error={results.error}
            onRetry={() => void results.refetch()}
            // The reference's answer when the header box held a telephone number nobody has.
            emptyText={
              /^\d{10}$/.test(q) ? 'Given MDN is not associated with any customer.' : undefined
            }
            page={page}
            pageSize={PAGE_SIZE}
            total={results.data?.total ?? 0}
            onPageChange={(p) => update({ page: p > 1 ? String(p) : undefined })}
            pageSizeOptions={[PAGE_SIZE]}
            sort={sort}
            onSortChange={(s) => update({ sort: s, page: undefined })}
          />
        </CustomerRowLinks>
      ) : null}
    </Box>
  );
}
